import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import consumer from "../channels/consumer"

// Subscribes this viewer to the board's PresenceChannel so the server can show
// their circle to everyone, and heartbeats every 30s so an active viewer isn't
// reaped as stale. The circles themselves arrive over the board's Turbo stream;
// here we keep the subscription alive and ring our own circle (the shared
// broadcast is identical for everyone, so "you" can only be marked client-side).
//
// This controller is also the board's self-healing hook. Stream broadcasts are
// fire-and-forget: anything sent while the cable was down, or while Chrome had
// this tab frozen, is lost for good. Since this is the page's only app-level
// subscription, it is where reconnects become visible — so after a reconnect,
// and when the tab resurfaces from a long spell hidden, we re-fetch the page
// (a morph, so typing/scroll survive) instead of trusting we missed nothing.
export default class extends Controller {
  static values = { boardId: Number, userId: Number }

  connect() {
    this.wasDisconnected = false
    this.hiddenAt = null

    this.subscription = consumer.subscriptions.create(
      { channel: "PresenceChannel", board_id: this.boardIdValue },
      {
        connected: () => {
          // Refresh now that our subscription (and the board's Turbo stream)
          // are confirmed, then keep a steady heartbeat going.
          this.subscription.perform("touch")
          this.startHeartbeat()
          if (this.wasDisconnected) this.revalidate()
          this.wasDisconnected = false
        },
        disconnected: () => {
          this.wasDisconnected = true
        }
      }
    )

    this.onVisibility = () => this.visibilityChanged()
    document.addEventListener("visibilitychange", this.onVisibility)

    // Re-apply the "you" ring whenever the list is replaced by a broadcast.
    this.observer = new MutationObserver(() => this.ringSelf())
    this.observer.observe(this.element, { childList: true, subtree: true })
    this.ringSelf()
  }

  disconnect() {
    clearInterval(this.heartbeat)
    document.removeEventListener("visibilitychange", this.onVisibility)
    this.observer?.disconnect()
    this.subscription?.unsubscribe()
  }

  startHeartbeat() {
    clearInterval(this.heartbeat)
    this.heartbeat = setInterval(() => this.subscription.perform("touch"), 30000)
  }

  // A tab coming back from hidden may have missed broadcasts without the cable
  // ever dropping (throttled timers, frozen page). Heartbeat immediately so our
  // circle returns, and re-fetch if we were away long enough to matter.
  visibilityChanged() {
    if (document.hidden) {
      this.hiddenAt = Date.now()
      return
    }

    this.subscription.perform("touch")
    if (this.hiddenAt && Date.now() - this.hiddenAt > 30000) this.revalidate()
    this.hiddenAt = null
  }

  revalidate() {
    Turbo.visit(window.location.href, { action: "replace" })
  }

  ringSelf() {
    this.element.querySelectorAll("[data-user-id]").forEach((circle) => {
      const isMe = Number(circle.dataset.userId) === this.userIdValue
      // An offset ring reads as "you" on any circle colour (a paper gap then a
      // navy ring); everyone else keeps the plain paper ring used for stacking.
      circle.classList.toggle("ring-navy", isMe)
      circle.classList.toggle("ring-offset-2", isMe)
      circle.classList.toggle("ring-offset-paper", isMe)
      circle.classList.toggle("ring-paper", !isMe)
    })
  }
}
